/** @format */

import React, { useEffect } from 'react';
import { PageContainer, ConfirmDialog, LoadingPage, Icons } from 'components';
import jMoment from 'moment-jalaali';
import createToast from 'components/Toast/Toast';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Typography, Grid, ButtonGroup } from '@mui/material';
import { useStyles } from './style';
import { useBusiness } from 'hooks';
import { DocItem } from '../components';
import Services from '../services';
import { REQUEST_STATUS } from '../constants/consts';
import useCustomHook from './hooks';
import { constBreadCrumbsTitle } from 'utility';

const Bills = () => {
  //* * States */

  const {
    targetRequest,
    setTargetRequest,
    docTypes,
    setDocTypes,
    docTypesLoading,
    setdocTypesLoading,
    canceling,
    setCanceling,
    loading,
    setLoading,
    disableDelete,
    setDisableDelete,
    isConfirmOpen,
    setIsConfirmOpen,
  } = useCustomHook();
  //* * Hooks */
  const navigate = useNavigate();
  const classes = useStyles();
  const params = useParams();
  const businessInfo = useBusiness();

useEffect(() => {
  getDocTypes();
}, []);

  useEffect(() => {
    if (!params?.id || !businessInfo?.id) return;
    getData(params?.id);
  }, [params?.id, businessInfo?.id]);

  useEffect(() => {
    if (!targetRequest) setDisableDelete(true);
    else {
      setDisableDelete(
        !!REQUEST_STATUS.find((v) => v.value === targetRequest?.status)?.disableDelete,
      );
    }
  }, [targetRequest]);

  //* * Api Requests */
  const getData = async (reqId) => {
    if (!reqId) {
      return createToast('خطا', 'شناسه درخواست یافت نشد!', 'danger');
    }

    try {
      setLoading(true);

      const res = await Services.getRequests({ requestId: reqId });
      if (res?.result?.length > 0) {
        setTargetRequest(res.result[0]);
      } else {
        createToast('خطا', 'درخواست مورد نظر یافت نشد!', 'danger');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getDocTypes = async () => {
    try {
      setdocTypesLoading(true);
      const filesList = await Services.getDocumentTypes();
      const types = filesList?.result ?? {};
      const reformedTypes = Object.keys(types).map((v) => ({
        label: types[v],
        value: v,
      }));
      setDocTypes(reformedTypes);
    } catch (error) {
      console.error(error);
    } finally {
      setdocTypesLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!targetRequest?.id) return;
    try {
      setCanceling(true);
      await Services.cancelRequest(targetRequest.id);
      setIsConfirmOpen(false);

      createToast('موفق', 'درخواست با موفقیت لغو شد', 'success');

      getData(targetRequest?.id);
    } catch (error) {
    } finally {
      setCanceling(false);
    }
  };

  const hint =
    'در این بخش می‌توانید اطلاعات و اسناد درخواست مورد نظر را مشاهده و در صورت امکان اسناد را حذف کنید.';

  return (
    <PageContainer
      title="جزئیات درخواست تایید یا ارتقای کسب‌وکار"
      pageKey="requestConfirmation"
      description={hint}
      breadcrumbs={constBreadCrumbsTitle.REQUEST_CONFIRMATION}
      meta={
        <ButtonGroup>
          {targetRequest?.id && !businessInfo?.parentBusiness ? (
            <Button
              onClick={() => setIsConfirmOpen(true)}
              className={classes.cancelReqButton}
              startIcon={<Icons icon='Close' color="#939393" size="20" />}
              disabled={targetRequest?.status !== 'BUSINESS_REQUEST_CONFIRMATION_NEW'}
              variant="outlined"
            >
              لغو درخواست
            </Button>
          ) : null}
          <Button
            onClick={() => navigate('/RequestConfirmation')}
            color="primary"
            endIcon={<Icons icon="CaretLeft" color="#2b3990" size="23" />}
            variant="outlined"
          >
            بازگشت
          </Button>
        </ButtonGroup>
      }
    >
      <ConfirmDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleCancelRequest}
        title="تایید لغو درخواست"
        content="آیا از لغو درخواست جاری اطمینان دارید؟"
        confirmButtonText="لغو درخواست"
        cancelButtonProps="انصراف"
        loading={canceling}
      />
      {loading || !businessInfo?.id ? (
        <LoadingPage />
      ) : !businessInfo?.parentBusiness ? (
        targetRequest ? (
          <>
            <Grid container item alignItems="center" className={classes.title} xs={12}>
              <span className={classes.titleIcon}>
                <Icons icon='Info' color="#667585" size="18" />
              </span>
              <Typography className={classes.titleText}>اطلاعات درخواست</Typography>
            </Grid>
            <Grid container xs={12} md={9} spacing={0}>
              <Grid item xs={12} md={6} lg={3} className={classes.detail}>
                <Typography className={classes.detailTitle}>شناسه</Typography>
                <Typography className={classes.detailValue}>{targetRequest?.id ?? '-'}</Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={3} className={classes.detail}>
                <Typography className={classes.detailTitle}>وضعیت</Typography>
                <Typography
                  className={`${classes.detailValue} ${targetRequest?.status === 'BUSINESS_REQUEST_CONFIRMATION_REJECTED'
                    ? classes.error
                    : ''
                    }`}
                >
                  {targetRequest?.status
                    ? REQUEST_STATUS?.find((v) => v?.value === targetRequest.status)?.label ?? '-'
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={3} className={classes.detail}>
                <Typography className={classes.detailTitle}>تاریخ درخواست</Typography>
                <Typography className={classes.detailValue}>
                  {targetRequest?.requestDate
                    ? jMoment(new Date(targetRequest.requestDate))?.format(
                      'dddd, jD jMMMM jYYYY',
                    ) ?? '-'
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6} lg={3} className={classes.detail}>
                <Typography className={classes.detailTitle}>تاریخ تعیین وضعیت</Typography>
                <Typography className={classes.detailValue}>
                  {targetRequest?.determineDate
                    ? jMoment(new Date(targetRequest.determineDate))?.format(
                      'dddd, jD jMMMM jYYYY',
                    ) ?? '-'
                    : '-'}
                </Typography>
              </Grid>
              <Grid item xs={12} className={classes.detail}>
                <Typography className={classes.detailTitle}>متن درخواست</Typography>
                <Typography className={classes.detailValue}>
                  {targetRequest?.requesterDescription ?? '-'}
                </Typography>
              </Grid>
              {targetRequest?.confirmerDescription ? (
                <Grid item xs={12} className={classes.detail}>
                  <Typography className={classes.detailTitle}>
                    {targetRequest?.status === 'BUSINESS_REQUEST_CONFIRMATION_REJECTED'
                      ? 'علت رد درخواست'
                      : 'توضیحات تاییدکننده'}
                  </Typography>
                  <Typography className={`${classes.detailValue} ${classes.error}`}>
                    {targetRequest?.confirmerDescription ?? '-'}
                  </Typography>
                </Grid>
              ) : (
                ''
              )}
            </Grid>
            <br />
            <br />
            <Grid container alignItems="center" className={classes.title}>
              <span className={classes.titleIcon} >
                <Icons icon="Document" color="#2b3990" size="20" />
              </span>
              <Typography className={classes.titleText}>اسناد و مدارک</Typography>
            </Grid>
            <Grid container item xs={12} spacing={3} className={classes.docItemsContainer}>
              {targetRequest?.businessDocumentSrvList?.length > 0
                ? targetRequest.businessDocumentSrvList.map((v, k) =>
                  v?.deleted ? null : (
                    <DocItem
                      key={v?.id ?? k}
                      refreshList={() => getData(targetRequest?.id)}
                      file={v}
                      isDocSrv
                      requestId={targetRequest?.id}
                      disableDelete={disableDelete}
                      docTypes={docTypes}
                    />
                  ),
                )
                : null}
            </Grid>
          </>
        ) : (
          <div>
            <br />
            <br />
            <Typography align="center" variant="h6">
              درخواست یافت نشد!
            </Typography>
          </div>
        )
      ) : (
        <Typography align="center" className={classes.notAvailabelBranch}>
          دسترسی به این بخش برای شعبه های کسب‌وکار، ممکن نیست!
        </Typography>
      )}
    </PageContainer>
  );
};

export default Bills;
